import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { getReportTemplatePreviews } from "./reportTemplatePreviews";

// ─── TemplatePreviewModal (redesigned) ───────────────────────────
function TemplatePreviewModal({
  template,
  allTemplates,
  selectedId,
  onSelect,
  onClose,
  onNavigate,
  logoSrc,
}) {
  const stripRef = useRef(null);
  const currentIndex = allTemplates.findIndex((tpl) => tpl.id === template?.id);
  const previousTemplate = currentIndex > 0 ? allTemplates[currentIndex - 1] : null;
  const nextTemplate =
    currentIndex >= 0 && currentIndex < allTemplates.length - 1
      ? allTemplates[currentIndex + 1]
      : null;
  const getSlotTemplate = (offset) =>
    allTemplates[(currentIndex + offset + allTemplates.length) % allTemplates.length];
  const slots = [
    { tpl: getSlotTemplate(-2), offset: -2 },
    { tpl: getSlotTemplate(-1), offset: -1 },
    { tpl: getSlotTemplate(0), offset: 0 },
    { tpl: getSlotTemplate(1), offset: 1 },
    { tpl: getSlotTemplate(2), offset: 2 },
  ];

  const slotStyle = (offset) => {
    const abs = Math.abs(offset);
    const scale = abs === 0 ? 1 : abs === 1 ? 0.8 : 0.62;
    const opacity = abs === 0 ? 1 : abs === 1 ? 0.52 : 0.2;
    const zIndex = abs === 0 ? 10 : abs === 1 ? 5 : 1;
    const translateX = offset * 175;
    const translateY = abs === 0 ? -2 : abs === 1 ? 8 : 14;

    return {
      position: "absolute",
      width: 160,
      height: 196,
      top: "50%",
      left: "50%",
      transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) scale(${scale})`,
      opacity,
      zIndex,
      borderRadius: 12,
      overflow: "hidden",
      border: abs === 0
        ? selectedId === template.id
          ? "2.5px solid #10b981"
          : "2.5px solid #0891b2"
        : "2px solid transparent",
      boxShadow: abs === 0
        ? "0 18px 44px rgba(15, 23, 42, 0.18), 0 4px 14px rgba(15, 23, 42, 0.08)"
        : "0 4px 14px rgba(15, 23, 42, 0.08)",
      background: "#fff",
      cursor: abs === 0 ? "default" : "pointer",
      transition: "all 0.32s cubic-bezier(0.34,1.12,0.64,1)",
    };
  };

  return (
    <div className="template-preview-modal-backdrop" onClick={onClose}>
      <div
        className="template-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Top bar: nav buttons + title ── */}
        <div className="tpm-topbar">
          <div className="tpm-topbar-left" />

          <div className="tpm-topbar-center">
            <span className="tpm-title">{template.name}</span>
            <span className="tpm-desc">{template.desc}</span>
          </div>

          <div className="tpm-topbar-right">
            <button
              className="tpm-select-btn"
              onClick={() => { onSelect(template.id); onClose(); }}
              disabled={selectedId === template.id}
            >
              {selectedId === template.id ? "✓ Selected" : "Use This Template"}
            </button>
            <button className="tpm-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* ── Carousel strip ── */}
        <div className="tpm-carousel-strip" ref={stripRef}>
          <button
            className="tpm-carousel-nav left"
            onClick={() => previousTemplate && onNavigate(previousTemplate)}
            disabled={!previousTemplate}
            aria-label="Previous template"
          >
            ‹
          </button>

          {slots.map(({ tpl, offset }) => (
            <div
              key={`${tpl.id}-${offset}`}
              style={slotStyle(offset)}
              onClick={() => {
                if (offset !== 0) {
                  onNavigate(tpl);
                }
              }}
              title={tpl.name}
            >
              <div className="tpm-thumb-preview">
                <div className="tpm-thumb-inner">{tpl.getPreview ? tpl.getPreview(logoSrc) : tpl.preview}</div>
              </div>
              <div className="tpm-thumb-label">{tpl.name}</div>
              {tpl.id === selectedId && (
                <span className="tpm-thumb-badge">✓</span>
              )}
            </div>
          ))}

          <button
            className="tpm-carousel-nav right"
            onClick={() => nextTemplate && onNavigate(nextTemplate)}
            disabled={!nextTemplate}
            aria-label="Next template"
          >
            ›
          </button>
        </div>

        {/* ── Full preview ── */}
        <div className="template-preview-modal-body">
          <div className="template-preview-stage">
            <div className="template-preview-stage-inner">
              {template.getFullPreview
                ? template.getFullPreview(logoSrc)
                : template.fullPreview || (template.getPreview ? template.getPreview(logoSrc) : template.preview)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DownloadTemplateCarousel({
  templates,
  selectedTemplateId,
  onSelectTemplate,
  onPreviewTemplate,
  logoSrc,
}) {
  const initialIndex = Math.max(
    0,
    templates.findIndex((tpl) => tpl.id === selectedTemplateId)
  );
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  useEffect(() => {
    const nextIndex = templates.findIndex((tpl) => tpl.id === selectedTemplateId);
    if (nextIndex >= 0) {
      setActiveIndex(nextIndex);
    }
  }, [templates, selectedTemplateId]);

  const total = templates.length;
  const activeTemplate = templates[activeIndex];

  const navigate = useCallback(
    (dir) => {
      setActiveIndex((prev) => (prev + dir + total) % total);
    },
    [total]
  );

  const slotTemplate = (offset) => templates[(activeIndex + offset + total) % total];
  const slots = [
    { tpl: slotTemplate(-2), offset: -2 },
    { tpl: slotTemplate(-1), offset: -1 },
    { tpl: slotTemplate(0), offset: 0 },
    { tpl: slotTemplate(1), offset: 1 },
    { tpl: slotTemplate(2), offset: 2 },
  ];

  const slotStyle = (offset) => {
    const abs = Math.abs(offset);
    const scale = abs === 0 ? 1 : abs === 1 ? 0.8 : 0.62;
    const opacity = abs === 0 ? 1 : abs === 1 ? 0.52 : 0.2;
    const zIndex = abs === 0 ? 10 : abs === 1 ? 5 : 1;
    const translateX = offset * 175;
    const translateY = abs === 0 ? -2 : abs === 1 ? 8 : 14;

    return {
      position: "absolute",
      width: 160,
      height: 228,
      top: "50%",
      left: "50%",
      transform: `translate(calc(-50% + ${translateX}px), calc(-50% + ${translateY}px)) scale(${scale})`,
      opacity,
      zIndex,
      borderRadius: 12,
      overflow: "hidden",
      border: abs === 0
        ? selectedTemplateId === activeTemplate.id
          ? "2.5px solid #0891b2"
          : "2.5px solid #0f172a"
        : "2px solid transparent",
      boxShadow: abs === 0
        ? "0 18px 44px rgba(15, 23, 42, 0.18), 0 4px 14px rgba(15, 23, 42, 0.08)"
        : "0 4px 14px rgba(15, 23, 42, 0.08)",
      background: "#fff",
      cursor: abs === 0 ? "default" : "pointer",
      transition: "all 0.32s cubic-bezier(0.34,1.12,0.64,1)",
    };
  };

  if (!activeTemplate) return null;

  return (
    <div className="download-template-carousel">
      <div className="download-template-carousel-stage">
        {slots.map(({ tpl, offset }) => (
          <div
            key={`${tpl.id}-${offset}`}
            style={slotStyle(offset)}
            onClick={() => {
              if (offset !== 0) {
                navigate(offset > 0 ? 1 : -1);
              }
            }}
          >
            <div className="download-template-card-preview">
              <div className="download-template-card-preview-inner">
                {tpl.getPreview ? tpl.getPreview(logoSrc) : tpl.preview}
              </div>
            </div>
          </div>
        ))}

        <button
          className="download-template-carousel-nav left"
          onClick={() => navigate(-1)}
          aria-label="Previous template"
        >
          ‹
        </button>
        <button
          className="download-template-carousel-nav right"
          onClick={() => navigate(1)}
          aria-label="Next template"
        >
          ›
        </button>
      </div>

      <div className="download-template-carousel-copy">
        <div className="download-template-carousel-name">{activeTemplate.name}</div>
        <div className="download-template-carousel-desc">{activeTemplate.desc}</div>

        <div className="download-template-carousel-dots">
          {templates.map((tpl, index) => (
            <button
              key={tpl.id}
              className={`download-template-carousel-dot ${index === activeIndex ? "active" : ""}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`View ${tpl.name}`}
            />
          ))}
        </div>

        <div className="download-template-carousel-actions">
          <button
            className="download-template-preview-btn"
            onClick={() => onPreviewTemplate(activeTemplate)}
          >
            Preview Template
          </button>
          <button
            className={`download-template-select-btn ${selectedTemplateId === activeTemplate.id ? "selected" : ""}`}
            onClick={() => onSelectTemplate(activeTemplate.id)}
          >
            {selectedTemplateId === activeTemplate.id ? "✓ Selected" : "Use This Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const styles = `
  .report-modal {
    background: white;
    border-radius: 12px;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
  .report-modal-header {
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
    color: white;
    border-bottom: none;
    padding: 20px 25px;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .report-modal-title { font-size: 18px; font-weight: 600; margin: 0; letter-spacing: 0.5px; }
  .report-close-button {
    background: none; border: none; color: white; font-size: 24px; cursor: pointer;
    padding: 0; width: 30px; height: 30px; display: flex; align-items: center;
    justify-content: center; border-radius: 50%; transition: all 0.3s ease; opacity: 0.8;
  }
  .report-close-button:hover { background: rgba(255,255,255,0.15); opacity: 1; }
  .report-modal-body { padding: 25px; background: #fafafa; }
  .report-subtitle { font-size: 14px; color: #6b7280; margin-bottom: 25px; text-align: center; }
  .report-container {
    max-width: 750px; margin: 0 auto; background: white; padding: 30px;
    border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); border: 1px solid #e5e7eb;
  }
  .report-section-title { font-size: 20px; font-weight: 600; color: #374151; margin-bottom: 25px; text-align: center; }
  .report-dropdown-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
  .report-dropdown-wrapper { display: flex; flex-direction: column; gap: 8px; }
  .report-dropdown-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .report-select {
    font-size: 14px; color: #374151; border: 1.5px solid #d1d5db; border-radius: 8px;
    padding: 12px 15px; background: white; transition: all 0.3s ease; width: 100%;
  }
  .report-select:focus { outline: none; border-color: #9ca3af; box-shadow: 0 0 0 3px rgba(156,163,175,0.1); }
  .report-settings-container { display: flex; flex-direction: column; gap: 18px; }
  .report-setting-card {
    background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px;
    padding: 20px; transition: all 0.3s ease;
  }
  .report-setting-card:hover { border-color: #d1d5db; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }
  .report-setting-label { display: block; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 12px; }
  .report-input {
    width: 100%; padding: 10px 12px; border: 1.5px solid #d1d5db; border-radius: 6px;
    font-size: 14px; transition: all 0.3s ease; box-sizing: border-box; background: white;
  }
  .report-input:focus { outline: none; border-color: #9ca3af; box-shadow: 0 0 0 3px rgba(156,163,175,0.1); }
  .report-checkbox { width: 18px; height: 18px; accent-color: #6b7280; cursor: pointer; }
  .report-image-container { display: flex; flex-direction: column; gap: 12px; }
  .report-image-preview {
    max-width: 200px; max-height: 150px; border: 1px solid #e5e7eb;
    border-radius: 8px; object-fit: cover; box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  }
  .report-image-filename { font-size: 12px; color: #6b7280; font-style: italic; margin-top: 4px; }
  .report-file-input {
    padding: 12px; border: 1.5px dashed #d1d5db; border-radius: 8px; background: #f9fafb;
    cursor: pointer; transition: all 0.3s ease; width: 100%; box-sizing: border-box;
    text-align: center; color: #6b7280;
  }
  .report-file-input:hover { background: #f3f4f6; border-color: #9ca3af; }
  .report-no-data {
    text-align: center; padding: 40px 20px; color: #6b7280; font-style: italic;
    background: #f9fafb; border-radius: 12px; border: 1.5px dashed #d1d5db;
  }
  .report-save-button {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; border: none;
    padding: 12px 30px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer;
    transition: all 0.3s ease; margin-top: 20px; display: block; margin-left: auto; margin-right: auto;
  }
  .report-save-button:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(139,92,246,0.25); }
  .report-save-button:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
  .report-modal-footer {
    background: white; border-top: 1px solid #e5e7eb; padding: 20px 25px;
    border-radius: 0 0 12px 12px; position: sticky; bottom: 0; z-index: 10;
  }
  .report-download-container { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; width: 100%; }
  .report-download-button {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; border: none;
    padding: 15px 20px; border-radius: 8px; font-weight: 500; font-size: 12px; cursor: pointer;
    transition: all 0.3s ease; text-transform: uppercase; letter-spacing: 0.5px;
  }
  .report-download-button:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(59,130,246,0.25); }
  .report-download-button.pdf { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  .report-download-button.pdf:hover { box-shadow: 0 4px 12px rgba(16,185,129,0.25); }

  /* ── Template selector ── */
  .template-selector-section { margin-top: 28px; padding-top: 22px; border-top: 1px solid #e5e7eb; }
  .template-selector-header { margin-bottom: 10px; }
  .template-selector-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .download-template-carousel {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }
  .download-template-carousel-stage {
    position: relative;
    width: 100%;
    height: 300px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .download-template-card-preview {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #f8fafc;
  }
  .download-template-card-preview-inner {
    transform: scale(0.205);
    transform-origin: top left;
    width: 780px;
    pointer-events: none;
  }
  .download-template-carousel-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 1px solid #dbe4ee;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
    color: #334155;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  .download-template-carousel-nav.left { left: 8px; }
  .download-template-carousel-nav.right { right: 8px; }
  .download-template-carousel-copy {
    width: 100%;
    max-width: 520px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
  .download-template-carousel-name {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
  }
  .download-template-carousel-desc {
    font-size: 13px;
    color: #6b7280;
  }
  .download-template-carousel-dots {
    display: flex;
    gap: 6px;
    margin-top: 2px;
  }
  .download-template-carousel-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    border: none;
    background: #d1d5db;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s ease;
  }
  .download-template-carousel-dot.active {
    width: 22px;
    background: #0891b2;
  }
  .download-template-carousel-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 6px;
  }
  .download-template-preview-btn,
  .download-template-select-btn {
    min-height: 40px;
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s ease;
  }
  .download-template-preview-btn {
    border: 1.5px solid #cbd5e1;
    background: #fff;
    color: #334155;
  }
  .download-template-select-btn {
    border: 1.5px solid #0891b2;
    background: #fff;
    color: #0891b2;
  }
  .download-template-select-btn.selected {
    background: #0891b2;
    color: #fff;
  }

  /* ── Preview modal backdrop ── */
  .template-preview-modal-backdrop {
    position: fixed; inset: 0; background: rgba(17,24,39,0.65); z-index: 1200;
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .template-preview-modal {
    width: min(1100px, 100%); max-height: 92vh; overflow: hidden; background: white;
    border-radius: 16px; box-shadow: 0 24px 64px rgba(0,0,0,0.3);
    display: flex; flex-direction: column;
  }

  /* ── TPM top bar ── */
  .tpm-topbar {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 20px; border-bottom: 1px solid #e2e8f0;
    background: #ffffff; flex-shrink: 0;
  }
  .tpm-topbar-left { width: 96px; flex-shrink: 0; }
  .tpm-topbar-center {
    flex: 1; display: flex; flex-direction: column; align-items: center;
    gap: 2px; min-width: 0;
  }
  .tpm-title { font-size: 15px; font-weight: 700; color: #111827; }
  .tpm-desc { font-size: 12px; color: #6b7280; }
  .tpm-topbar-right { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }

  .tpm-select-btn {
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%); color: white;
    border: none; border-radius: 8px; padding: 8px 16px; font-size: 12px;
    font-weight: 600; cursor: pointer; transition: all 0.2s ease; white-space: nowrap;
  }
  .tpm-select-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(8,145,178,0.28); }
  .tpm-select-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none; box-shadow: none; }

  .tpm-close-btn {
    width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #e5e7eb;
    background: white; color: #6b7280; font-size: 20px; line-height: 1;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all 0.15s ease; flex-shrink: 0;
  }
  .tpm-close-btn:hover { background: #fee2e2; border-color: #fca5a5; color: #dc2626; }

  /* ── TPM thumbnail strip ── */
  .tpm-carousel-strip {
    position: relative;
    width: 100%;
    height: 250px;
    overflow: hidden;
    flex-shrink: 0;
    border-bottom: 1px solid #e2e8f0;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  }
  .tpm-carousel-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 20;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: 1px solid #dbe4ee;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
    color: #334155;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .tpm-carousel-nav.left { left: 18px; }
  .tpm-carousel-nav.right { right: 18px; }
  .tpm-carousel-nav:hover:not(:disabled) {
    border-color: #0891b2;
    color: #0891b2;
    background: #f0f9ff;
  }
  .tpm-carousel-nav:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .tpm-thumb-preview {
    width: 100%; height: 90px; overflow: hidden; background: #f1f5f9;
    position: relative;
  }
  .tpm-thumb-inner {
    transform: scale(0.19);
    transform-origin: top left;
    width: 780px;
    pointer-events: none;
  }
  .tpm-thumb-label {
    padding: 7px 9px; font-size: 11px; font-weight: 600; color: #1e293b;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    border-top: 1px solid #f1f5f9; background: white; letter-spacing: 0.01em;
  }
  .tpm-thumb-badge {
    position: absolute; top: 6px; right: 6px; z-index: 2;
    background: #10b981; color: white; font-size: 10px; font-weight: 800;
    width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 6px rgba(16,185,129,0.45);
  }

  /* ── TPM body ── */
  .template-preview-modal-body {
    padding: 0; overflow: auto; background: #eef2f7; flex: 1;
    min-height: 0;
  }
  .template-preview-stage {
    width: 780px; margin: 0 auto;
    overflow: visible;
  }
  .template-preview-stage-inner {
    width: 780px;
  }

  @media (max-width: 700px) {
    .report-dropdown-container { grid-template-columns: 1fr; gap: 15px; }
    .report-container { padding: 20px; }
    .report-download-container { grid-template-columns: 1fr; gap: 10px; }
    .tpm-topbar { flex-wrap: wrap; }
    .tpm-topbar-center { order: -1; width: 100%; align-items: flex-start; }
    .tpm-carousel-strip { height: 220px; }
    .download-template-carousel-stage { height: 258px; }
    .download-template-carousel-actions { width: 100%; }
    .download-template-preview-btn,
    .download-template-select-btn { width: 100%; }
  }
`;

// ─── Component ───────────────────────────────────────────────────
export default function DownloadReportModal({ show, onClose, financialYears = [], frameworks = [] }) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState("");
  const [selectedFrameworkId, setSelectedFrameworkId] = useState("");
  const [settings, setSettings] = useState([]);
  const [selectedFrameworkSettings, setSelectedFrameworkSettings] = useState([]);
  const [originalFrameworkSettings, setOriginalFrameworkSettings] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(1);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const logoSrc = useMemo(() => {
    const imgSetting = selectedFrameworkSettings.find(
      s => s.settingMetaAndAnswer?.type === "image"
    );
    if (!imgSetting) return null;
    const meta = imgSetting.settingMetaAndAnswer;
    return meta.fileBody || meta.value || null;
  }, [selectedFrameworkSettings]);

  const selectedFinancialYearValue = useMemo(() => {
    return (
      financialYears.find(year => String(year.id) === selectedFinancialYearId)?.financial_year_value ||
      financialYears[0]?.financial_year_value ||
      ""
    );
  }, [financialYears, selectedFinancialYearId]);

  const REPORT_TEMPLATE_PREVIEWS = useMemo(
    () => getReportTemplatePreviews(logoSrc, selectedFinancialYearValue),
    [logoSrc, selectedFinancialYearValue]
  );

  const isEqualSettings = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const fetchSettings = useCallback(async () => {
    if (!frameworks.length) return;
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}generateReport/settings`,
        {},
        { frameworkIds: frameworks.map(f => f.id) }
      );
      if (isSuccess && Array.isArray(data?.reportGenerationSettings)) {
        setSettings(data.reportGenerationSettings);
      }
    } catch (error) {
      console.error("Error fetching settings", error);
    }
  }, [frameworks]);

  useEffect(() => {
    if (financialYears.length) setSelectedFinancialYearId(String(financialYears[0].id));
    if (frameworks.length) { setSelectedFrameworkId(String(frameworks[0].id)); fetchSettings(); }
  }, [financialYears, frameworks, fetchSettings]);

  useEffect(() => {
    if (settings.length && selectedFinancialYearId && selectedFrameworkId) {
      const filtered = settings.filter(
        s => String(s.frameworkId) === selectedFrameworkId && String(s.financialYearId) === selectedFinancialYearId
      );
      setSelectedFrameworkSettings(filtered);
      setOriginalFrameworkSettings(JSON.parse(JSON.stringify(filtered)));
      setIsDirty(false);
    } else {
      setSelectedFrameworkSettings([]);
      setOriginalFrameworkSettings([]);
    }
  }, [settings, selectedFinancialYearId, selectedFrameworkId]);

  useEffect(() => {
    setIsDirty(!isEqualSettings(selectedFrameworkSettings, originalFrameworkSettings));
  }, [selectedFrameworkSettings, originalFrameworkSettings]);

  useEffect(() => {
    if (!show) setPreviewTemplate(null);
  }, [show]);

  useEffect(() => {
    if (!previewTemplate) return;
    const updatedTemplate = REPORT_TEMPLATE_PREVIEWS.find(tpl => tpl.id === previewTemplate.id);
    if (updatedTemplate && updatedTemplate !== previewTemplate) {
      setPreviewTemplate(updatedTemplate);
    }
  }, [REPORT_TEMPLATE_PREVIEWS, previewTemplate]);

  const handleValueChange = (index, newValue) => {
    setSelectedFrameworkSettings(prev => {
      const updated = [...prev];
      const currentMeta = updated[index]?.settingMetaAndAnswer || {};
      if (currentMeta.readOnly || currentMeta.disabled) return prev;
      updated[index] = { ...updated[index], settingMetaAndAnswer: { ...updated[index].settingMetaAndAnswer, value: newValue } };
      return updated;
    });
  };

  const handleImageUpload = (index, file) => {
    const currentMeta = selectedFrameworkSettings[index]?.settingMetaAndAnswer || {};
    if (currentMeta.readOnly || currentMeta.disabled) return;
    if (file.size > 500 * 1024) { alert("File size exceeds 500KB. Please upload a smaller image."); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFrameworkSettings(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], settingMetaAndAnswer: { ...updated[index].settingMetaAndAnswer, fileBody: reader.result, fileName: file.name, value: undefined } };
        return updated;
      });
    };
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}generateReport/settings`,
        {},
        { reportGenerationSettings: selectedFrameworkSettings },
        "POST"
      );
      if (isSuccess) { setOriginalFrameworkSettings(JSON.parse(JSON.stringify(selectedFrameworkSettings))); setIsDirty(false); }
    } catch (error) { console.error("Error saving settings", error); }
  };

  const resetSettings = () => {
    setSelectedFrameworkSettings(JSON.parse(JSON.stringify(originalFrameworkSettings)));
    setIsDirty(false);
  };

  /**
   * Downloads a static report bundled in the public folder by fetching it into
   * a blob and triggering an anchor click. The relative path resolves against
   * the app's own origin, and the same-origin blob URL guarantees a download.
   */
  const downloadStaticReport = (fileName) => {
    console.log('inside download')
    fetch(`/${fileName}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading report", error);
      });
  };

  const generateAndOpenPdfReport = async () => {
    downloadStaticReport("BRSR_Report_20260723.pdf");
    return;
    try {
      setIsLoading(true);
      const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
      const response = await axios.post(
        `${config.POSTLOGIN_API_URL_COMPANY}generateReport/pdf`,
        { financialYearId: Number(selectedFinancialYearId), frameworkId: Number(selectedFrameworkId), reportType: "PDF", templateId: selectedTemplateId },
        {
          responseType: "blob",
          withCredentials: true,
          headers: {
            Accept: "application/pdf",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            userId: currentUser?.id,
          },
        }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const contentDisposition = response.headers["content-disposition"] || "";
      const fileName = contentDisposition.match(/filename="([^"]+)"/i)?.[1] || "BRSR_Report.pdf";
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl; link.download = fileName; link.rel = "noopener noreferrer";
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) { console.error("Error generating PDF report", error); }
    finally { setIsLoading(false); }
  };

  const generateAndDownloadDocxReport = async () => {
    downloadStaticReport("BRSR_Report_20260723.docx");
    return;
    try {
      setIsLoading(true);
      const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
      const response = await axios.post(
        `${config.POSTLOGIN_API_URL_COMPANY}generateReport/docx`,
        { financialYearId: Number(selectedFinancialYearId), frameworkId: Number(selectedFrameworkId), reportType: "DOCX", templateId: selectedTemplateId },
        {
          responseType: "blob",
          withCredentials: true,
          headers: {
            Accept: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            userId: currentUser?.id,
          },
        }
      );
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const contentDisposition = response.headers["content-disposition"] || "";
      const fileName = contentDisposition.match(/filename="([^"]+)"/i)?.[1] || "BRSR_Report.docx";
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl; link.download = fileName; link.rel = "noopener noreferrer";
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) { console.error("Error generating report", error); }
    finally { setIsLoading(false); }
  };

  if (!frameworks.length || !financialYears.length) return null;

  return (
    <>
      <style>{styles}</style>
      <Modal size="lg" show={show} onHide={onClose}>
        <div className="report-modal">

          {isLoading && (
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(255,255,255,0.75)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
              <div style={{ width: 40, height: 40, border: "4px solid rgba(0,0,0,0.1)", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              <span style={{ marginTop: 10, fontSize: 16, fontWeight: 600 }}>Generating report…</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          <div className="report-modal-header">
            <h4 className="report-modal-title">ESG REPORT GENERATOR</h4>
            <button className="report-close-button" onClick={onClose}>×</button>
          </div>

          <div className="report-modal-body">
            <p className="report-subtitle">Configure and download your ESG compliance reports</p>

            <div className="report-container">
              <h2 className="report-section-title">Report Configuration</h2>

              <div className="report-dropdown-container">
                <div className="report-dropdown-wrapper">
                  <label className="report-dropdown-label">Financial Year</label>
                  <select className="report-select" onChange={e => setSelectedFinancialYearId(e.target.value)} value={selectedFinancialYearId}>
                    {financialYears.map(year => (
                      <option key={year.id} value={String(year.id)}>FY – {year.financial_year_value}</option>
                    ))}
                  </select>
                </div>
                <div className="report-dropdown-wrapper">
                  <label className="report-dropdown-label">Framework</label>
                  <select className="report-select" onChange={e => setSelectedFrameworkId(e.target.value)} value={selectedFrameworkId}>
                    {frameworks.map(framework => (
                      <option key={framework.id} value={String(framework.id)}>{framework.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="report-settings-container">
                {selectedFrameworkSettings.length > 0 ? (
                  selectedFrameworkSettings.map((setting, index) => {
                    const meta = setting.settingMetaAndAnswer;
                    const isLocked = !!(meta.readOnly || meta.disabled);
                    return (
                      <div key={setting.settingName} className="report-setting-card">
                        <label className="report-setting-label">{meta.label}</label>
                        {meta.type === "checkbox" && (
                          <input type="checkbox" className="report-checkbox" checked={!!meta.value} onChange={e => handleValueChange(index, e.target.checked)} disabled={isLocked} />
                        )}
                        {meta.type === "text" && (
                          <input type="text" className="report-input" value={meta.value || ""} onChange={e => handleValueChange(index, e.target.value)} placeholder={`Enter ${meta.label.toLowerCase()}`} readOnly={isLocked} disabled={isLocked} />
                        )}
                        {meta.type === "image" && (
                          <div className="report-image-container">
                            {(meta.value || meta.fileBody) && (
                              <div>
                                <img src={meta.value || meta.fileBody} alt={meta.label} className="report-image-preview" />
                                {meta.fileName && <div className="report-image-filename">Current file: {meta.fileName}</div>}
                              </div>
                            )}
                            <input type="file" accept="image/*" className="report-file-input" onChange={e => e.target.files && handleImageUpload(index, e.target.files[0])} disabled={isLocked} />
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="report-no-data">
                    <p>No configuration settings found for the selected year and framework.</p>
                    <small>Please try selecting a different combination or contact support.</small>
                  </div>
                )}
              </div>

              {selectedFrameworkSettings.length > 0 && (
                <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                  <button onClick={resetSettings} className="report-save-button" style={{ background: "#6b7280" }} disabled={!isDirty}>Reset</button>
                  <button onClick={saveSettings} className="report-save-button" disabled={!isDirty}>Save Configuration</button>
                </div>
              )}

              <div className="template-selector-section">
                <div className="template-selector-header">
                  <div className="template-selector-label">Report Template</div>
                </div>

                <DownloadTemplateCarousel
                  templates={REPORT_TEMPLATE_PREVIEWS}
                  selectedTemplateId={selectedTemplateId}
                  onSelectTemplate={setSelectedTemplateId}
                  onPreviewTemplate={setPreviewTemplate}
                  logoSrc={logoSrc}
                />

                {previewTemplate && (
                  <TemplatePreviewModal
                    template={previewTemplate}
                    allTemplates={REPORT_TEMPLATE_PREVIEWS}
                    selectedId={selectedTemplateId}
                    onSelect={(id) => setSelectedTemplateId(id)}
                    onClose={() => setPreviewTemplate(null)}
                    onNavigate={(tpl) => setPreviewTemplate(tpl)}
                    logoSrc={logoSrc}
                  />
                )}
              </div>

            </div>
          </div>

          <div className="report-modal-footer">
            <div className="report-download-container">
              <button className="report-download-button" onClick={generateAndDownloadDocxReport}>
                📄 Download Word Document
              </button>
              <button className="report-download-button pdf" onClick={generateAndOpenPdfReport}>
                📋 Download PDF Report
              </button>
            </div>
          </div>

        </div>
      </Modal>
    </>
  );
}
